import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Briefcase,
  Image as ImageIcon,
  Users,
  MessageSquareQuote,
  Newspaper,
  Inbox,
  Mic,
  Settings,
  LogOut,
} from "lucide-react";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/blogs", label: "Blogs", icon: Newspaper },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/packages", label: "Podcast", icon: Mic },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#030712] text-white grid grid-cols-1 lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:flex flex-col bg-[#0B1F3A] border-r border-white/10">
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2F6BFF] to-[#4DA8FF] flex items-center justify-center font-bold">R</span>
            <div>
              <div className="text-sm font-semibold">Rise Rift</div>
              <div className="text-[10px] uppercase tracking-widest text-blue-400">Control Center</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {items.map((i) => {
            const Icon = i.icon;
            return (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.end}
                data-testid={`admin-nav-${i.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                    isActive
                      ? "bg-[#2F6BFF]/15 text-white border border-[#2F6BFF]/30"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {i.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-slate-400">Signed in as</div>
            <div className="text-sm font-medium truncate">{user?.email}</div>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:bg-white/5 hover:text-white transition"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center justify-between p-4 bg-[#0B1F3A] border-b border-white/10">
          <span className="font-semibold">Rise Rift Admin</span>
          <button onClick={handleLogout} className="text-xs text-slate-300" data-testid="admin-logout-mobile">Logout</button>
        </div>
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
